import type { Customer, FilterResult } from '../types';
import type { SimpleFilterCriteria } from '@/components/pipeline/SimplePipelineFilters';
import { isPhoneMissing } from '@shared/phone-quality';

/**
 * Default filter criteria for pipeline
 */
export const pipelineFilterDefaults: SimpleFilterCriteria = {
  search: '',
  leadScore: 'all',
  source: 'all',
  recentLeads: 'last30days',
  needsContact: 'all',
  sortBy: 'dateAdded',
  sortOrder: 'asc',
};

/**
 * Recursively extract string values from nested objects
 * Limited to 3 levels deep to prevent performance issues
 */
function extractNestedStrings(obj: any, depth = 0, maxDepth = 3): string[] {
  if (depth > maxDepth || !obj || typeof obj !== 'object') {
    return [];
  }

  const parts: string[] = [];
  for (const value of Object.values(obj)) {
    if (typeof value === 'string' && value.length < 500) {
      parts.push(value);
    } else if (Array.isArray(value)) {
      value.forEach(item => {
        if (typeof item === 'string' && item.length < 500) {
          parts.push(item);
        } else if (typeof item === 'object') {
          parts.push(...extractNestedStrings(item, depth + 1, maxDepth));
        }
      });
    } else if (typeof value === 'object') {
      parts.push(...extractNestedStrings(value, depth + 1, maxDepth));
    }
  }
  return parts;
}

// WeakMap cache for additionalData text extraction - auto-cleans when customer objects are GC'd
const additionalDataTextCache = new WeakMap<object, string>();

/**
 * Pre-extract searchable text from additionalData object
 * This avoids JSON.stringify on every filter pass which is expensive for large datasets
 * Uses recursive extraction to find nested string values (up to 3 levels deep)
 * Results are cached per-object via WeakMap for repeated searches
 */
export function extractAdditionalDataText(additionalData: any): string {
  if (!additionalData || typeof additionalData !== 'object') {
    return '';
  }

  const cached = additionalDataTextCache.get(additionalData);
  if (cached !== undefined) {
    return cached;
  }

  const parts = extractNestedStrings(additionalData);
  const result = parts.join(' ');
  additionalDataTextCache.set(additionalData, result);
  return result;
}

/**
 * Build a pre-computed search haystack for a customer.
 * This is everything except the stage name (which is dynamic and appended at search time).
 * Called once when customer data loads, not on every keystroke.
 */
export function buildCustomerHaystack(customer: Customer): string {
  const parts = [
    customer.name,
    customer.email,
    customer.phone,
    customer.address,
    customer.postcode,
    customer.notes,
    customer.pipelineNotes,
    customer.source,
    customer.status,
  ];

  const additional = extractAdditionalDataText(customer.additionalData);
  if (additional) {
    parts.push(additional);
  }

  return parts.filter(Boolean).join(' ').toLowerCase();
}

export interface ComputeFilterResultParams {
  customers: Customer[];
  filters: SimpleFilterCriteria;
  searchQuery: string;
  focusMatchesOnly: boolean;
  stageNameById: Map<number, string>;
}

/**
 * Compute filtered customers and matching IDs based on filter criteria
 */
export function computeFilterResult({
  customers,
  filters,
  searchQuery,
  focusMatchesOnly,
  stageNameById,
}: ComputeFilterResultParams): FilterResult {
  const defaultResult: FilterResult = {
    filteredCustomers: [],
    matchingIds: new Set<number>(),
    searchActive: false,
    highlightTerms: [],
  };

  if (!customers || !Array.isArray(customers)) {
    return defaultResult;
  }

  let working = [...customers];

  // Lead score filter (star-based)
  if (filters.leadScore && filters.leadScore !== 'all') {
    const targetScore = parseInt(filters.leadScore);
    working = working.filter((customer) => {
      if (filters.leadScore === '0') {
        return !customer.leadScore || customer.leadScore === 0;
      }
      return customer.leadScore === targetScore;
    });
  }

  // Recent leads filter - uses pre-computed timestamps for performance
  if (filters.recentLeads && filters.recentLeads !== 'all') {
    const now = Date.now();
    let cutoffTimestamp: number;

    switch (filters.recentLeads) {
      case 'today':
        cutoffTimestamp = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
        break;
      case 'week':
        cutoffTimestamp = now - 7 * 24 * 60 * 60 * 1000;
        break;
      case 'last30days':
        cutoffTimestamp = now - 30 * 24 * 60 * 60 * 1000;
        break;
      case 'month':
        cutoffTimestamp = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
        break;
      default:
        cutoffTimestamp = 0;
    }

    working = working.filter(
      (customer) => (customer.createdAtTimestamp || 0) >= cutoffTimestamp
    );
  }

  // Contact status filter - uses pre-computed timestamps for performance
  if (filters.needsContact && filters.needsContact !== 'all') {
    const sevenDaysAgoTimestamp = Date.now() - 7 * 24 * 60 * 60 * 1000;

    working = working.filter((customer) => {
      // Blank or placeholder numbers, so someone can chase the real one.
      if (filters.needsContact === 'missing_phone') return isPhoneMissing(customer.phone);
      const hasBeenContacted = customer.lastContactDate !== null;

      if (filters.needsContact === 'never_contacted') {
        return !hasBeenContacted;
      } else if (filters.needsContact === 'needs_followup') {
        if (!hasBeenContacted) return true;
        // Use pre-computed timestamp instead of creating new Date
        return (customer.lastContactTimestamp || 0) < sevenDaysAgoTimestamp;
      }
      return true;
    });
  }

  // Source filter
  if (filters.source && filters.source !== 'all') {
    working = working.filter((customer) => {
      if (filters.source === 'null') {
        return !customer.source || customer.source === null;
      }
      return customer.source === filters.source;
    });
  }

  // Build highlight terms from search inputs
  const highlightTerms = [searchQuery, filters.search]
    .filter((term): term is string => !!term && term.trim().length > 0)
    .map((term) => term.trim());

  const normalizedTerms = highlightTerms.map((term) => term.toLowerCase());
  const matchingIds = new Set<number>();

  let finalCustomers = working;
  if (normalizedTerms.length > 0) {
    const next: Customer[] = [];
    for (const customer of working) {
      // Use pre-built haystack (computed once when data loads) + dynamic stage name
      let haystack = customer._searchHaystack;
      if (!haystack) {
        // Fallback: build on the fly if haystack wasn't pre-computed
        haystack = buildCustomerHaystack(customer);
      }

      // Append stage name (only dynamic part that changes per-stage)
      const stageName = customer.pipelineStageId
        ? stageNameById.get(customer.pipelineStageId)
        : undefined;
      const fullHaystack = stageName ? haystack + ' ' + stageName.toLowerCase() : haystack;

      const isMatch = normalizedTerms.every((term) => fullHaystack.includes(term));

      if (isMatch) {
        matchingIds.add(customer.id);
      }

      if (!focusMatchesOnly || isMatch) {
        next.push(customer);
      }
    }

    finalCustomers = next;
  }

  // Sorting - uses pre-computed timestamps for performance
  const now = Date.now();
  finalCustomers.sort((a, b) => {
    let aValue: number | string = 0;
    let bValue: number | string = 0;

    switch (filters.sortBy) {
      case 'leadScore':
        aValue = a.leadScore || 0;
        bValue = b.leadScore || 0;
        break;
      case 'dateAdded':
        // Use pre-computed timestamp instead of creating new Date
        aValue = a.createdAtTimestamp || (filters.sortOrder === 'desc' ? now : 0);
        bValue = b.createdAtTimestamp || (filters.sortOrder === 'desc' ? now : 0);
        break;
      case 'name':
        aValue = a.name?.toLowerCase() || '';
        bValue = b.name?.toLowerCase() || '';
        break;
      case 'dealValue':
        aValue = a.dealValue || 0;
        bValue = b.dealValue || 0;
        break;
      case 'stageUpdated':
        // Use pre-computed timestamp instead of creating new Date
        aValue = a.pipelineUpdatedAtTimestamp || 0;
        bValue = b.pipelineUpdatedAtTimestamp || 0;
        break;
      case 'nextContact':
        // Use pre-computed timestamp instead of creating new Date
        aValue = a.nextContactTimestamp ?? Number.POSITIVE_INFINITY;
        bValue = b.nextContactTimestamp ?? Number.POSITIVE_INFINITY;
        break;
      default:
        aValue = a.leadScore || 0;
        bValue = b.leadScore || 0;
        break;
    }

    if (filters.sortOrder === 'asc') {
      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    }
    return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
  });

  return {
    filteredCustomers: finalCustomers,
    matchingIds,
    searchActive: normalizedTerms.length > 0,
    highlightTerms,
  };
}
